import React from 'react';
import { useDraggable } from '@dnd-kit/core';
import { format } from 'date-fns';
import { he } from 'date-fns/locale';
import { Download, FileText, CheckCircle2, Circle, GripVertical } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { ProjectFile } from '@/types';

interface DocumentTreeItemProps {
  file: ProjectFile;
  isSelected: boolean;
  isAndroid: boolean;
  selectionMode: boolean;
  /** Mirrors DocumentsTab.tileHandlers — Android long-press / web click semantics. */
  tileHandlers: (id: string, onTap?: () => void) => Record<string, unknown>;
  onFileClick: (file: ProjectFile) => void;
  onToggleSelection: (id: string) => void;
  onDownload: (file: ProjectFile) => void;
  /** Depth-based indentation (inline-start) in the tree. */
  depth: number;
}

export default function DocumentTreeItem({
  file,
  isSelected,
  isAndroid,
  selectionMode,
  tileHandlers,
  onFileClick,
  onToggleSelection,
  onDownload,
  depth,
}: DocumentTreeItemProps) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: file.id,
    data: { type: 'file', fileId: file.id },
  });

  return (
    <div
      ref={setNodeRef}
      style={{ marginInlineStart: depth * 20 }}
      {...tileHandlers(file.id, () => onFileClick(file))}
      className={cn(
        'bg-card rounded-xl p-3 border border-border flex items-center gap-3 cursor-pointer hover:bg-secondary/50 transition-colors group',
        isAndroid && 'select-none',
        isSelected && 'ring-2 ring-primary bg-primary/5',
        isDragging && 'opacity-40',
      )}
    >
      {/* Drag handle — only this grip starts a drag, so row taps still select/preview. */}
      <button
        type="button"
        {...listeners}
        {...attributes}
        onClick={(e) => e.stopPropagation()}
        className="flex-shrink-0 text-muted-foreground/50 hover:text-muted-foreground cursor-grab active:cursor-grabbing touch-none"
        aria-label="גרור קובץ"
      >
        <GripVertical className="w-4 h-4" />
      </button>

      <div className="w-9 h-9 rounded-lg bg-secondary flex items-center justify-center flex-shrink-0">
        <FileText className="w-4 h-4 text-muted-foreground" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium truncate text-sm">{file.name}</p>
        <p className="text-xs text-muted-foreground">
          {format(new Date(file.createdAt), 'd בMMMM yyyy', { locale: he })}
        </p>
      </div>

      {isAndroid && selectionMode ? (
        <div className="flex-shrink-0 pointer-events-none">
          {isSelected ? (
            <CheckCircle2 className="w-6 h-6 text-primary" />
          ) : (
            <Circle className="w-6 h-6 text-muted-foreground/60" />
          )}
        </div>
      ) : (
        <>
          {!isAndroid && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onToggleSelection(file.id);
              }}
              className={cn(
                'flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity',
                isSelected && 'opacity-100',
              )}
              aria-label={isSelected ? 'בטל בחירה' : 'בחר'}
            >
              {isSelected ? (
                <CheckCircle2 className="w-6 h-6 text-primary" />
              ) : (
                <Circle className="w-6 h-6 text-muted-foreground/60" />
              )}
            </button>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="flex-shrink-0"
            onClick={(e) => {
              e.stopPropagation();
              onDownload(file);
            }}
          >
            <Download className="w-4 h-4" />
          </Button>
        </>
      )}
    </div>
  );
}
